import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Button } from '~/components/ui';

function parseDateInput(value: string): Date {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function toDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

type ConfirmOrderModalProps = {
  initialShippedDate: Date;
  initialDeliveryStartDate: Date;
  initialDeliveryEndDate: Date | null;
  onConfirm: (
    shippedDate: Date,
    deliveryStartDate: Date,
    deliveryEndDate: Date | null
  ) => void;
  onCancel: () => void;
  isConfirming?: boolean;
};

export function ConfirmOrderModal({
  initialShippedDate,
  initialDeliveryStartDate,
  initialDeliveryEndDate,
  onConfirm,
  onCancel,
  isConfirming = false,
}: ConfirmOrderModalProps) {
  const { t } = useTranslation('order');
  const [shippedDate, setShippedDate] = useState<Date>(initialShippedDate);
  const [deliveryStartDate, setDeliveryStartDate] = useState<Date>(
    initialDeliveryStartDate
  );
  const [deliveryEndDate, setDeliveryEndDate] = useState<Date | null>(
    initialDeliveryEndDate
  );

  const confirmDisabled =
    isConfirming ||
    deliveryStartDate < shippedDate ||
    (deliveryEndDate !== null && deliveryEndDate < deliveryStartDate);

  return (
    <div
      className="bg-neut-bg-alt rounded-lg w-md p-6"
      data-testid="confirm-order-modal"
    >
      <h3 className="text-xl text-neut-prim font-semibold mb-4">
        {t('order.confirm_order')}
      </h3>
      <div className="space-y-4">
        <div>
          <label
            htmlFor="ship-date-input"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            {t('order.ship_date')} *
          </label>
          <div className="relative">
            <input
              id="ship-date-input"
              type="date"
              data-testid="ship-date-input"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prim"
              value={toDateInputValue(shippedDate)}
              onChange={(e) => {
                if (e.target.value && e.target.value !== '') {
                  setShippedDate(parseDateInput(e.target.value));
                }
              }}
            />
          </div>
        </div>
        <div>
          <label
            htmlFor="delivery-start-date-input"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            {t('order.estimated_delivery_start')} *
          </label>
          <div className="relative">
            <input
              id="delivery-start-date-input"
              type="date"
              data-testid="delivery-start-date-input"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prim"
              value={toDateInputValue(deliveryStartDate)}
              onChange={(e) => {
                if (e.target.value && e.target.value !== '') {
                  setDeliveryStartDate(parseDateInput(e.target.value));
                }
              }}
            />
          </div>
        </div>
        <div>
          <label
            htmlFor="delivery-end-date-input"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            {t('order.estimated_delivery_end')}
          </label>
          <div className="relative">
            <input
              id="delivery-end-date-input"
              type="date"
              data-testid="delivery-end-date-input"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prim"
              value={deliveryEndDate ? toDateInputValue(deliveryEndDate) : ''}
              onChange={(e) =>
                setDeliveryEndDate(
                  e.target.value ? parseDateInput(e.target.value) : null
                )
              }
            />
          </div>
        </div>
        <div className="flex gap-3 pt-2">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              onConfirm(shippedDate, deliveryStartDate, deliveryEndDate)
            }
            disabled={confirmDisabled}
            className="bg-prim"
          >
            {t('order.confirm_order')}
          </Button>
        </div>
      </div>
    </div>
  );
}
